import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Moon, Sun, Link2, Check, SquareTerminal } from 'lucide-react';
import McpConnectDialog, { copyToClipboard } from './McpConnectDialog';
import McpKeysService from '../services/mcpKeysService';

const FloatingActions = () => {
  const [isDark, setIsDark] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [mcpEnabled, setMcpEnabled] = useState(false);
  const [mcpDialogOpen, setMcpDialogOpen] = useState(false);
  const copiedTimerRef = useRef(null);

  // Проверяем текущую тему при загрузке
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = savedTheme === 'dark' || (!savedTheme && systemPrefersDark);

    setIsDark(shouldBeDark);
    document.documentElement.classList.toggle('dark', shouldBeDark);
  }, []);

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  // Кнопка «Подключить к Claude Code» видна, только если сервер включил MCP
  useEffect(() => {
    let cancelled = false;
    McpKeysService.getStatus()
      .then((status) => {
        if (!cancelled) setMcpEnabled(status?.enabled === true);
      })
      .catch(() => {
        if (!cancelled) setMcpEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);

    // Сохраняем в localStorage
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');

    // Применяем к документу
    document.documentElement.classList.toggle('dark', newTheme);
  };

  // Адресная строка всегда описывает текущий экран (раздел, вкладка, фильтры),
  // поэтому шарить достаточно её
  const copyLink = async () => {
    await copyToClipboard(window.location.href);

    setLinkCopied(true);
    clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {/* Подключение Claude Code к данным дашборда (персональный MCP-ключ) */}
      {mcpEnabled && (
        <Button
          variant="outline"
          size="icon"
          onClick={() => setMcpDialogOpen(true)}
          title="Подключить к Claude Code"
          aria-label="Подключить к Claude Code"
          className="h-10 w-10 rounded-full shadow-lg bg-background border-border hover:bg-accent transition-all duration-200"
        >
          <SquareTerminal className="h-4 w-4 transition-transform duration-200" />
        </Button>
      )}

      {/* Ссылка на текущий экран */}
      <Button
        variant="outline"
        size="icon"
        onClick={copyLink}
        title="Скопировать ссылку на этот экран (раздел, вкладка, фильтры)"
        aria-label="Скопировать ссылку на этот экран"
        className="h-10 w-10 rounded-full shadow-lg bg-background border-border hover:bg-accent transition-all duration-200"
      >
        {linkCopied ? (
          <Check className="h-4 w-4 text-emerald-500 transition-transform duration-200" />
        ) : (
          <Link2 className="h-4 w-4 transition-transform duration-200" />
        )}
      </Button>

      {/* Переключатель темы */}
      <Button
        variant="outline"
        size="icon"
        onClick={toggleTheme}
        title={isDark ? 'Светлая тема' : 'Тёмная тема'}
        aria-label="Переключить тему"
        className="h-10 w-10 rounded-full shadow-lg bg-background border-border hover:bg-accent transition-all duration-200"
      >
        {isDark ? (
          <Sun className="h-4 w-4 transition-transform duration-200" />
        ) : (
          <Moon className="h-4 w-4 transition-transform duration-200" />
        )}
      </Button>

      <McpConnectDialog open={mcpDialogOpen} onOpenChange={setMcpDialogOpen} />
    </div>
  );
};

export default FloatingActions;
